/**
 * What a printed email contains: one entry per message, headers + body.
 * Built from the loaded thread when there is one, else from the open email.
 * Nothing interactive (order cards, AI banners, tabs, buttons) belongs here.
 *
 * In a thread every reply quotes the messages before it, so each reply
 * prints only what its sender wrote (quoted history removed, see
 * emailQuoteStripping) while the OLDEST message always prints in full:
 * a forwarded PO at the root of the thread is never lost.
 */
import type { AttachmentMeta, ChainEmail } from '../types/portal';
import { formatDateTime } from './dateFormatters';
import { stripQuotedHtml, stripQuotedText } from './emailQuoteStripping';
import { bareEmailAddress, senderAddressSuffix } from './senderDisplay';

export interface PrintableEmailMessage {
  id: string;
  subject: string | null;
  from: string;
  to: string | null;
  cc: string | null;
  /** Display date, e.g. "Sep 28, 2026, 2:58 PM". */
  date: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  attachmentNames: string[];
  /** True when the quoted earlier messages were removed from this body. */
  quotedHidden: boolean;
}

/** The email open in the viewer, when no thread was loaded for it. */
export interface OpenEmailForPrint {
  id: string;
  subject: string | null;
  from: string;
  to?: string | null;
  cc?: string | null;
  receivedAt?: string | null;
  /** Fallback date label when receivedAt is missing (e.g. "8:50 PM"). */
  timeLabel?: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  attachments?: AttachmentMeta[];
}

function nonEmpty(value: string | null | undefined): string | null {
  return value && value.trim() ? value : null;
}

/** `Name <addr>`, or the bare address when there is no distinct name. The
 *  orders chain can pass a raw `Name <addr>` header as from_address. */
export function formatSender(
  name: string | null | undefined,
  address: string | null | undefined,
): string {
  const suffix = senderAddressSuffix(name, address);
  const n = nonEmpty(name);
  if (n && suffix) return `${n} <${suffix}>`;
  return bareEmailAddress(address) ?? n ?? '';
}

function fromChain(email: ChainEmail): PrintableEmailMessage {
  return {
    id: email.id,
    subject: nonEmpty(email.subject),
    from: formatSender(email.from_name, email.from_address),
    to: nonEmpty(email.to_address),
    cc: nonEmpty(email.cc_addresses),
    date: email.received_at ? formatDateTime(email.received_at) : null,
    bodyText: nonEmpty(email.body_text),
    bodyHtml: nonEmpty(email.body_html),
    attachmentNames: (email.attachments ?? []).map((a) => a.filename),
    quotedHidden: false,
  };
}

/** The same message with the quoted history removed from whichever body
 *  prints. Exported for single-message prints (Sent page) that want the
 *  sender's own words rather than the whole quoted conversation. */
export function withoutQuotedHistory(m: PrintableEmailMessage): PrintableEmailMessage {
  if (m.bodyHtml) {
    const r = stripQuotedHtml(m.bodyHtml);
    return r.stripped ? { ...m, bodyHtml: r.body, quotedHidden: true } : m;
  }
  if (m.bodyText) {
    const r = stripQuotedText(m.bodyText);
    return r.stripped ? { ...m, bodyText: r.body, quotedHidden: true } : m;
  }
  return m;
}

/** Index of the oldest message: earliest received_at, else the last one
 *  (the chain API returns newest first). */
export function oldestIndex(chain: ChainEmail[]): number {
  let best = -1;
  let bestTime = Infinity;
  chain.forEach((e, i) => {
    const t = e.received_at ? Date.parse(e.received_at) : NaN;
    if (!isNaN(t) && t < bestTime) {
      bestTime = t;
      best = i;
    }
  });
  return best >= 0 ? best : chain.length - 1;
}

function fromOpenEmail(email: OpenEmailForPrint): PrintableEmailMessage {
  return {
    id: email.id,
    subject: nonEmpty(email.subject),
    from: email.from,
    to: nonEmpty(email.to),
    cc: nonEmpty(email.cc),
    date: email.receivedAt ? formatDateTime(email.receivedAt) : nonEmpty(email.timeLabel),
    bodyText: nonEmpty(email.bodyText),
    bodyHtml: nonEmpty(email.bodyHtml),
    attachmentNames: (email.attachments ?? []).map((a) => a.filename),
    quotedHidden: false,
  };
}

/**
 * Messages to print, newest first (the order the thread view shows). A
 * loaded thread wins because it carries every message's full body; the
 * open email is the fallback when the thread is empty or failed to load.
 * Replies print without their quoted history; the oldest message is whole.
 */
export function buildPrintableMessages(
  chain: ChainEmail[],
  openEmail: OpenEmailForPrint,
): PrintableEmailMessage[] {
  if (chain.length === 0) return [fromOpenEmail(openEmail)];
  const oldest = oldestIndex(chain);
  return chain.map((e, i) => (i === oldest ? fromChain(e) : withoutQuotedHistory(fromChain(e))));
}
